import { useRef, useState } from "react";
import axiosInstance, {
  isAxiosError,
  setAxiosToken,
} from "../../../../../../common/infrastructure/helpers/axiosHelper";
import { useAuthentication } from "../../../../../../common/infrastructure/hooks/useAuthentication";
import { endpointsMetadata } from "../../../../../../common/infrastructure/routes/endpointsMetadata";
import { uploadImageFile } from "../../../../../../common/infrastructure/utilities/imageUploadUtility";
import {
  getErrorToast,
  getSuccessToast,
} from "../../../../../../common/infrastructure/utilities/toastUtility";

/** Kept in step with the server's own cap, so an oversized file fails before it is uploaded. */
const MAX_LOGO_SIZE_IN_BYTES = 5 * 1024 * 1024;

/**
 * Upload and removal of the studio's logo. The file goes straight to the API rather than through
 * the wizard's form, because it is stored the moment it is picked — not when the form is saved.
 */
export const useStudioLogo = (studioId: number, studioLogoUrl: string | null) => {
  const { currentUser } = useAuthentication();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  // The uploaded logo is shown from the URL the API just returned, so the preview updates
  // without waiting for the wizard's studio query to come back.
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(null);
  const [isRemoved, setIsRemoved] = useState(false);

  // Deliberately NOT refetching the wizard's studio query after a change: that query re-seeds
  // every field of the wizard form, so refreshing it here would throw away whatever the studio
  // had typed but not yet saved. Local state is enough to keep the preview honest.
  const logoUrl = isRemoved ? null : uploadedLogoUrl ?? studioLogoUrl;

  const openFilePicker = () => fileInputRef.current?.click();

  const handleError = (error: unknown, fallbackMessage: string) => {
    if (isAxiosError(error) && error.response?.data?.message) {
      getErrorToast(error.response.data.message);
      return;
    }

    getErrorToast(fallbackMessage);
  };

  const uploadLogo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (file.size > MAX_LOGO_SIZE_IN_BYTES) {
      getErrorToast("Логото може да е най-много 5 MB.");
      event.target.value = "";
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadImageFile<{ url: string }>(
        `${endpointsMetadata.studio.uploadLogo}?studioId=${studioId}`,
        file,
        currentUser?.authToken,
      );

      setUploadedLogoUrl(result?.url ?? null);
      setIsRemoved(false);
      getSuccessToast("Логото беше качено успешно.");
    } catch (error) {
      handleError(error, "Възникна грешка при качването на логото.");
    } finally {
      setIsUploading(false);

      // Without this, picking the same file twice in a row fires no change event.
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeLogo = async () => {
    setIsUploading(true);

    try {
      setAxiosToken(currentUser?.authToken);

      await axiosInstance.delete(
        `${endpointsMetadata.studio.deleteLogo}?studioId=${studioId}`,
      );

      setUploadedLogoUrl(null);
      setIsRemoved(true);
      getSuccessToast("Логото беше премахнато успешно.");
    } catch (error) {
      handleError(error, "Възникна грешка при премахването на логото.");
    } finally {
      setIsUploading(false);
    }
  };

  return {
    fileInputRef,
    logoUrl,
    isUploading,
    openFilePicker,
    uploadLogo,
    removeLogo,
  };
};
